/**
 * Moteur de calcul de l'impôt sur le revenu, pour les simulateurs de /outils.
 *
 * Isolé de toute interface : c'est du calcul pur, donc testable, et c'est la
 * seule pièce dont l'exactitude engage vraiment. Les valeurs viennent du
 * référentiel arbitré (docs/glossaries/micro-entreprise/reference-2026.json)
 * et de nulle part ailleurs.
 *
 * LA RÈGLE QUE LES SIMULATEURS GRAND PUBLIC RATENT
 *
 * En versement libératoire, le chiffre d'affaires sort de la base imposable,
 * mais il reste retenu pour déterminer le TAUX appliqué aux autres revenus du
 * foyer. C'est l'article 197 C du CGI, dite règle du taux effectif, posée pour
 * préserver la progressivité de l'impôt.
 *
 *     impôt sur les autres revenus = IR(revenu total) × (autres revenus ÷ revenu total)
 *
 * L'ignorer sous-estime l'impôt d'un foyer qui a des salaires à côté, et fait
 * conclure à tort que l'option est gagnante. La fiche Service-Public F23267 ne
 * mentionne pas cette règle : elle est au BOFiP, BOI-BIC-DECLA-10-40-20.
 */

/** Barème 2026 sur les revenus 2025, par part de quotient familial. */
export const BAREME_2026 = [
    { jusqua: 11600, taux: 0 },
    { jusqua: 29579, taux: 0.11 },
    { jusqua: 84577, taux: 0.3 },
    { jusqua: 181917, taux: 0.41 },
    { jusqua: Infinity, taux: 0.45 },
];

/** Plafond de l'avantage procuré par chaque demi-part supplémentaire. */
export const PLAFOND_DEMI_PART_2026 = 1807;

/**
 * Plafond de la PART ENTIÈRE du premier enfant d'un parent isolé (case T).
 *
 * LE PREMIER ENFANT D'UN PARENT ISOLÉ OUVRE UNE PART, PAS UNE DEMI-PART. Une
 * personne seule qui élève un enfant compte donc 2 parts, et 2,5 avec deux
 * enfants : la majoration d'une part vaut pour le premier seulement, les
 * suivants ouvrant une demi-part ordinaire. L'avantage de cette part entière
 * est plafonné à part, et plus haut que les autres.
 *
 * Sans cette règle, un simulateur donne 1,5 part à un parent isolé d'un enfant
 * et surestime lourdement son impôt : à 45 000 € de chiffre d'affaires en BNC,
 * 1 068 € annoncés contre 142 € dus, et le comparateur recommande alors le
 * versement libératoire là où le barème fait gagner 848 €.
 */
export const PLAFOND_PART_PARENT_ISOLE_2026 = 4262;

/**
 * Décote sur l'impôt brut (article 197, I-4 du CGI), revenus 2025.
 *
 * LA CORRECTION QUI RENVERSE LE VERDICT DES PETITS REVENUS. Sans elle, un
 * simulateur surestime l'impôt de tout foyer dont l'impôt brut reste sous le
 * seuil, et fait conclure à tort que le versement libératoire est gagnant : à
 * 30 000 € de chiffre d'affaires en BNC, seul, l'impôt au barème tombe de 902 €
 * à 413 €, sous les 660 € du versement libératoire.
 *
 * Le forfait dépend de l'imposition seule ou commune, pas des enfants. Elle
 * s'applique après le plafonnement du quotient familial et avant les
 * réductions d'impôt. Source : BOI-IR-LIQ-20-20-30 du 7 avril 2026.
 */
export const DECOTE_2026 = {
    seul: { seuil: 1982, forfait: 897 },
    couple: { seuil: 3277, forfait: 1483 },
    taux: 0.4525,
};

/** Montant de la décote pour un impôt brut donné, nul au-delà du seuil. */
export function decote(impotBrut, enCouple) {
    const d = enCouple ? DECOTE_2026.couple : DECOTE_2026.seul;
    if (impotBrut >= d.seuil) return 0;
    return Math.min(impotBrut, Math.max(0, d.forfait - DECOTE_2026.taux * impotBrut));
}

/** Abattement forfaitaire et taux de versement libératoire, par activité. */
export const ACTIVITES = {
    vente: { abattement: 0.71, vfl: 0.01 },
    services: { abattement: 0.5, vfl: 0.017 },
    bnc: { abattement: 0.34, vfl: 0.022 },
};

/** Minimum d'abattement, porté au double en activité mixte. */
export const ABATTEMENT_MINIMUM = 305;

/**
 * Revenu fiscal de référence par part à ne pas dépasser pour opter, par année
 * d'application de l'option.
 *
 * Deux montants coexistent et ce ne sont pas deux versions du même chiffre :
 * ce sont deux années différentes. La règle est un double décalage, posé par
 * l'article 151-0 du CGI : le RFR de l'AVANT-DERNIÈRE année est comparé à la
 * limite de la deuxième tranche du barème de l'année précédente. D'où 29 315 €
 * pour une option applicable en 2026, lus sur le RFR de 2024, et 29 579 € pour
 * 2027, lus sur celui de 2025.
 *
 * Les années antérieures ne figurent pas ici : elles n'ont pas été confirmées à
 * une source officielle, et le référentiel interdit de chiffrer ce qu'on ne
 * peut pas confirmer. Elles n'ont de toute façon plus d'usage, la date limite
 * d'option étant passée.
 */
export const RFR_PLAFONDS = {
    2026: { plafond: 29315, anneeRfr: 2024, anneeAvis: 2025 },
    2027: { plafond: 29579, anneeRfr: 2025, anneeAvis: 2026 },
};

/** Année d'application par défaut, la plus récente sur laquelle on peut agir. */
export const ANNEE_OPTION_PAR_DEFAUT = 2027;

/** Les seules années dont le plafond a été confirmé à une source officielle. */
export const ANNEES_RFR_CONNUES = Object.keys(RFR_PLAFONDS).map(Number);

/**
 * Le barème d'une année, ou `null` si elle n'est pas connue.
 *
 * REFUSER VAUT MIEUX QUE REPLIER. Le plafond de l'année N se lit sur la limite
 * supérieure de la deuxième tranche du barème de N-1 (CGI art. 151-0) : il
 * n'existe donc pas avant la publication de ce barème, et aucune règle ne
 * permet de le deviner. Un repli silencieux sur l'année par défaut ferait
 * répondre l'outil pour une année dont la date limite d'option est passée,
 * sans le dire, à partir du 1er octobre 2027.
 */
export function baremeRfr(annee) {
    return RFR_PLAFONDS[annee] || null;
}

/** Conservé pour le comparateur, qui raisonne sur l'année en cours. */
export const RFR_PLAFOND_2026 = RFR_PLAFONDS[2026].plafond;

/** Impôt brut sur un revenu déjà divisé par le nombre de parts. */
function impotParPart(revenuParPart) {
    let du = 0;
    let plancher = 0;
    for (const tranche of BAREME_2026) {
        if (revenuParPart <= plancher) break;
        const hauteur = Math.min(revenuParPart, tranche.jusqua) - plancher;
        du += hauteur * tranche.taux;
        plancher = tranche.jusqua;
    }
    return du;
}

/**
 * Parts de quotient familial. Les deux premiers enfants valent une demi-part,
 * les suivants une part entière.
 *
 * `parentIsole` est la case T de la déclaration : une personne seule qui élève
 * ses enfants gagne une demi-part de plus, celle qui fait du premier enfant une
 * part entière. Elle n'a de sens que hors couple et avec au moins un enfant.
 */
export function parts({ enCouple, enfants = 0, parentIsole = false }) {
    const base = enCouple ? 2 : 1;
    const apport = Math.min(enfants, 2) * 0.5 + Math.max(0, enfants - 2) * 1;
    const majorationIsole = estParentIsole({ enCouple, enfants, parentIsole }) ? 0.5 : 0;
    return base + apport + majorationIsole;
}

/** La case T ne joue que pour une personne seule ayant au moins un enfant. */
export function estParentIsole({ enCouple, enfants = 0, parentIsole = false }) {
    return Boolean(parentIsole) && !enCouple && enfants >= 1;
}

/**
 * Impôt sur le revenu d'un foyer, plafonnement du quotient familial et décote
 * compris.
 *
 * Le plafonnement se calcule comme l'administration le fait : on compare
 * l'impôt aux parts réelles à celui des parts sans enfant, et l'écart, qui est
 * l'avantage procuré par les enfants, est ramené au plafond s'il le dépasse.
 * Sans lui, un simulateur surestime l'avantage des foyers avec enfants.
 */
export function impotSurLeRevenu(revenuImposable, { enCouple, enfants = 0, parentIsole = false }) {
    const brut = impotBrut(revenuImposable, { enCouple, enfants, parentIsole });
    return brut - decote(brut, enCouple);
}

/**
 * Impôt brut : barème et plafonnement du quotient familial, avant décote.
 *
 * Le plafonnement n'est pas uniforme. Chez un parent isolé, la part entière du
 * premier enfant a son propre plafond, plus élevé, et seules les demi-parts
 * suivantes retombent au plafond ordinaire. Les compter toutes au même tarif
 * rognerait un avantage que la loi accorde.
 */
export function impotBrut(revenuImposable, { enCouple, enfants = 0, parentIsole = false }) {
    if (revenuImposable <= 0) return 0;

    const isole = estParentIsole({ enCouple, enfants, parentIsole });
    const partsReelles = parts({ enCouple, enfants, parentIsole });
    const partsSansEnfant = enCouple ? 2 : 1;

    const avecEnfants = impotParPart(revenuImposable / partsReelles) * partsReelles;
    if (enfants === 0) return avecEnfants;

    const sansEnfants = impotParPart(revenuImposable / partsSansEnfant) * partsSansEnfant;
    const avantage = sansEnfants - avecEnfants;

    // Chez un parent isolé, la part entière du premier enfant vaut deux
    // demi-parts et porte à elle seule le plafond majoré ; le reste suit le
    // plafond ordinaire.
    const demiPartsAuTarifOrdinaire =
        (partsReelles - partsSansEnfant) / 0.5 - (isole ? 2 : 0);
    const plafond =
        demiPartsAuTarifOrdinaire * PLAFOND_DEMI_PART_2026 +
        (isole ? PLAFOND_PART_PARENT_ISOLE_2026 : 0);

    return avantage > plafond ? sansEnfants - plafond : avecEnfants;
}

/**
 * Bénéfice imposable d'une activité micro, après abattement forfaitaire.
 *
 * L'article 50-0 du CGI fixe un minimum d'ABATTEMENT de 305 €, pas un minimum
 * de bénéfice : sur 400 € de chiffre d'affaires en BNC, l'abattement vaut 305 €
 * et non 136 €, et le bénéfice 95 €. La première version l'écrivait à l'envers
 * et garantissait 305 € de bénéfice à qui n'avait presque rien encaissé.
 */
export function beneficeMicro(chiffreAffaires, activite) {
    const { abattement } = ACTIVITES[activite];
    const retenu = Math.min(
        chiffreAffaires,
        Math.max(chiffreAffaires * abattement, ABATTEMENT_MINIMUM),
    );
    return Math.max(0, chiffreAffaires - retenu);
}

/**
 * Compare le versement libératoire et l'imposition au barème.
 *
 * @returns {{ benefice, revenuTotal, impotBareme, impotVfl, ecart, gagnant,
 *             vflSurCa, impotAutresRevenusEnVfl, tauxMoyen, bascule }}
 */
export function comparerVersementLiberatoire({
    chiffreAffaires,
    activite,
    autresRevenus = 0,
    enCouple = false,
    enfants = 0,
    parentIsole = false,
}) {
    const { vfl } = ACTIVITES[activite];
    const benefice = beneficeMicro(chiffreAffaires, activite);
    const revenuTotal = benefice + autresRevenus;
    const foyer = { enCouple, enfants, parentIsole };

    // Sans option : tout est imposé au barème.
    const impotBareme = impotSurLeRevenu(revenuTotal, foyer);

    // Avec option : un forfait sur le chiffre d'affaires, et les autres revenus
    // imposés au taux effectif du revenu total.
    const vflSurCa = chiffreAffaires * vfl;
    const impotAutresRevenusEnVfl =
        revenuTotal > 0 ? impotBareme * (autresRevenus / revenuTotal) : 0;
    const impotVfl = vflSurCa + impotAutresRevenusEnVfl;

    const ecart = impotBareme - impotVfl;

    return {
        benefice,
        revenuTotal,
        impotBareme,
        impotVfl,
        vflSurCa,
        impotAutresRevenusEnVfl,
        ecart,
        gagnant: Math.abs(ecart) < 1 ? "egalite" : ecart > 0 ? "vfl" : "bareme",
        tauxMoyen: revenuTotal > 0 ? impotBareme / revenuTotal : 0,
        // Taux moyen d'imposition au-dessus duquel l'option devient gagnante.
        // Il ne dépend ni du chiffre d'affaires ni du foyer : c'est le rapport
        // entre le taux de versement et la part imposable du chiffre d'affaires.
        bascule: vfl / (1 - ACTIVITES[activite].abattement),
    };
}

/**
 * Éligibilité à l'option, sur le revenu fiscal de référence de N-2.
 *
 * Le plafond se multiplie par le nombre de parts. Le code des impôts l'écrit
 * comme une majoration de 50 % par demi-part supplémentaire, ce qui revient
 * exactement au même : un couple à deux parts est bien à deux fois le plafond.
 *
 * Le nombre de parts accepte aussi une valeur libre, pour les foyers dont le
 * quotient comporte des quarts de part (garde alternée) que le calcul par
 * situation familiale ne sait pas reconstituer.
 */
export function eligible({
    revenuFiscalReference,
    enCouple,
    enfants = 0,
    parentIsole = false,
    partsFournies = null,
    annee = ANNEE_OPTION_PAR_DEFAUT,
}) {
    const bareme = baremeRfr(annee);
    const nombreDeParts = partsFournies ?? parts({ enCouple, enfants, parentIsole });

    // Pas de barème, pas de verdict : le dire est la seule réponse honnête.
    if (!bareme) {
        return {
            eligible: null,
            baremeInconnu: true,
            parts: nombreDeParts,
            annee,
        };
    }

    const plafond = bareme.plafond * nombreDeParts;
    return {
        eligible: revenuFiscalReference <= plafond,
        baremeInconnu: false,
        plafond,
        // L'écart au plafond, que le composant recalculait de son côté.
        ecartAuPlafond: Math.abs(revenuFiscalReference - plafond),
        parts: nombreDeParts,
        plafondParPart: bareme.plafond,
        anneeRfr: bareme.anneeRfr,
        anneeAvis: bareme.anneeAvis,
        annee,
    };
}

/**
 * Jours restants avant la date limite d'option pour une année donnée.
 *
 * L'option se prend au plus tard le 30 septembre de l'année précédente. La date
 * du jour est passée en paramètre plutôt que lue ici : une fonction pure se
 * teste, `new Date()` non.
 */
export function joursAvantEcheance(annee, aujourdhui) {
    const limite = new Date(Date.UTC(annee - 1, 8, 30, 23, 59, 59));
    const debut = new Date(
        Date.UTC(
            aujourdhui.getUTCFullYear(),
            aujourdhui.getUTCMonth(),
            aujourdhui.getUTCDate(),
        ),
    );
    return Math.ceil((limite - debut) / 86400000);
}

/* ------------------------------------------------------------------------- */
/* ACRE : exonération de début d'activité                                     */
/* ------------------------------------------------------------------------- */

/**
 * Taux de cotisations PAYÉS pendant l'ACRE, et non pourcentages d'exonération.
 *
 * La réforme divise l'aide par deux, et la bascule ne dépend ni du profil ni du
 * chiffre d'affaires : elle dépend de la DATE de début d'activité. Deux
 * créateurs identiques séparés d'une semaine ne paient pas la même chose
 * pendant plus d'un an.
 */
export const TAUX_ACRE = {
    avant: {
        limite: "2026-06-30",
        vente: 0.062,
        services: 0.106,
        bnc: 0.128,
        cipav: 0.134,
    },
    apres: {
        vente: 0.093,
        services: 0.159,
        bnc: 0.192,
        cipav: 0.174,
    },
};

/** Taux pleins correspondants, hors ACRE. */
export const TAUX_PLEINS = {
    vente: 0.123,
    services: 0.212,
    bnc: 0.256,
    cipav: 0.232,
};

/** Délai pour déposer la demande, en jours à compter du début d'activité. */
export const ACRE_DELAI_DEMANDE_JOURS = 60;

/**
 * Une reprise d'activité est-elle un début d'activité au sens de l'ACRE ?
 *
 * DEUX RÈGLES DIFFÉRENTES SE CONFONDENT ICI, et seule la seconde était connue :
 *
 *   - L'article L. 131-6-4, IV interdit de bénéficier DEUX FOIS de l'ACRE dans
 *     un délai de trois ans, compté depuis la fin du bénéfice précédent.
 *   - L'article R. 131-3 dit tout autre chose : « ne sont assimilées à un début
 *     d'activité ni la modification des conditions d'exercice de l'activité
 *     professionnelle, ni la reprise d'activité intervenue soit dans l'année au
 *     cours de laquelle est survenue la cessation d'activité, soit dans l'année
 *     suivante ». L'ACRE étant une exonération de DÉBUT d'activité, elle n'est
 *     pas due faute de début, même à qui n'en a jamais bénéficié.
 *
 * Les deux se cumulent. Quelqu'un qui a cessé en mars 2026 sans jamais avoir eu
 * l'ACRE se la voit refuser pour toute reprise jusqu'au 31 décembre 2027, et
 * aucun des deux outils ne le lui disait.
 *
 * @param dateCessation date de cessation de l'activité précédente, ou null.
 * @param dateDebut date de début de la nouvelle activité.
 */
export function reprisePrecoce(dateCessation, dateDebut) {
    if (!dateCessation || !dateDebut) return { reprise: false };
    const anneeCessation = new Date(dateCessation).getUTCFullYear();
    const anneeDebut = new Date(dateDebut).getUTCFullYear();
    // L'année de la cessation, et celle qui la suit.
    const reprise = anneeDebut === anneeCessation || anneeDebut === anneeCessation + 1;
    return {
        reprise,
        anneeCessation,
        anneeDebut,
        // La date à partir de laquelle une reprise redevient un début.
        rouvreLe: `${anneeCessation + 2}-01-01`,
    };
}

/** Une création à compter du 1er juillet 2026 relève du régime réduit. */
export function regimeAcre(dateDebut) {
    return dateDebut <= new Date("2026-06-30T23:59:59Z") ? "avant" : "apres";
}

/**
 * Fin de l'exonération : dernier jour du troisième trimestre civil qui suit
 * celui du début d'activité. La durée réelle va donc de neuf à douze mois selon
 * la date, ce qui surprend toujours : commencer un 1er janvier ou un 31 mars
 * donne exactement la même date de fin.
 */
export function finAcre(dateDebut) {
    const trimestre = Math.floor(dateDebut.getUTCMonth() / 3);
    const total = trimestre + 3;
    const annee = dateDebut.getUTCFullYear() + Math.floor(total / 4);
    const moisFin = (total % 4) * 3 + 2;
    const dernierJour = new Date(Date.UTC(annee, moisFin + 1, 0));
    return dernierJour;
}

/**
 * Le délai de demande est-il encore ouvert ?
 *
 * Condition à part entière et non un simple rappel : depuis le 1er janvier
 * 2026 la demande est obligatoire, et passé soixante jours depuis le début
 * d'activité déclaré, le droit est perdu même quand toutes les autres
 * conditions sont réunies. C'est le refus le plus fréquent et le plus injuste,
 * puisqu'il ne tient qu'à une date.
 */
export function delaiDemandeOuvert(dateDebut, aujourdhui) {
    const limite = limiteDemandeAcre(dateDebut);
    const jours = Math.ceil((limite - aujourdhui) / 86400000);
    return { ouvert: jours >= 0, jours, limite };
}

/** Date limite de dépôt de la demande auprès de l'URSSAF. */
export function limiteDemandeAcre(dateDebut) {
    const limite = new Date(dateDebut.getTime());
    limite.setUTCDate(limite.getUTCDate() + ACRE_DELAI_DEMANDE_JOURS);
    return limite;
}

/**
 * Ce que l'ACRE fait gagner sur la période aidée.
 *
 * @param chiffreAffaires chiffre d'affaires attendu SUR LA PÉRIODE d'exonération,
 *   pas sur l'année : l'exonération s'épuise à date fixe, même sans encaissement.
 */
export function economieAcre({ chiffreAffaires, activite, dateDebut }) {
    const regime = regimeAcre(dateDebut);
    const tauxAide = TAUX_ACRE[regime][activite];
    const tauxPlein = TAUX_PLEINS[activite];

    const avecAide = chiffreAffaires * tauxAide;
    const sansAide = chiffreAffaires * tauxPlein;

    return {
        regime,
        tauxAide,
        tauxPlein,
        avecAide,
        sansAide,
        economie: sansAide - avecAide,
        // Part réellement exonérée : la moitié avant la réforme, le quart après.
        partExoneree: 1 - tauxAide / tauxPlein,
        fin: finAcre(dateDebut),
        limiteDemande: limiteDemandeAcre(dateDebut),
    };
}

/* ------------------------------------------------------------------------- */
/* Indemnités journalières maladie                                            */
/* ------------------------------------------------------------------------- */

/** Valeurs 2026, relevées sur ameli.fr (page vérifiée le 1er septembre 2026). */
export const IJ = {
    diviseur: 730,
    revenuMinimum: 4582,
    revenuPlafond: 48060,
    montantMaximal: 65.84,
    carence: 3,
    affiliationMois: 12,
    // Durées de PRESCRIPTION d'un arrêt, et non durée maximale
    // d'indemnisation : c'est ce qui explique qu'un arrêt long arrive par
    // tranches. Les deux pages ameli.fr, celle des artisans et commerçants et
    // celle des professions libérales, portent la même phrase.
    prescriptionInitiale: 31,
    prescriptionProlongation: 62,
};

/**
 * AUCUNE DURÉE MAXIMALE D'INDEMNISATION N'EST PUBLIÉE ICI, et c'est délibéré.
 * Les chiffres qui circulent, 360 indemnités sur trois ans, 90 jours, 87 jours
 * par arrêt, viennent de forums ou de sources secondaires, divergent entre eux
 * et selon la profession, et aucune page officielle consultée le 8 septembre
 * 2026 n'en publie un. Le référentiel range donc ce plafond parmi les sujets à
 * ne pas chiffrer. Ce module expose la prescription, qui elle est concordante.
 */

/**
 * Indemnité journalière maladie d'un micro-entrepreneur.
 *
 * LE POINT QUE PRESQUE PERSONNE NE FORMULE : le revenu retenu n'est pas le
 * chiffre d'affaires, c'est le chiffre d'affaires APRÈS abattement forfaitaire.
 * En vente, l'abattement de 71 % ne laisse que 29 % du chiffre d'affaires, si
 * bien qu'il faut encaisser près de 15 800 € par an pour ouvrir le moindre
 * droit, là où un libéral y arrive avec 6 900 €.
 *
 * @param chiffreAffairesMoyen chiffre d'affaires annuel moyen des trois années
 *   civiles précédentes.
 */
export function indemniteJournaliere({ chiffreAffairesMoyen, activite, jours = 30 }) {
    const revenuAnnuelMoyen = beneficeMicro(chiffreAffairesMoyen, activite);
    const revenuRetenu = Math.min(revenuAnnuelMoyen, IJ.revenuPlafond);

    const ouvre = revenuAnnuelMoyen >= IJ.revenuMinimum;
    const montant = ouvre
        ? Math.min(revenuRetenu / IJ.diviseur, IJ.montantMaximal)
        : 0;

    const joursIndemnises = Math.max(0, jours - IJ.carence);

    // Chiffre d'affaires qu'il faudrait atteindre pour ouvrir le droit : c'est
    // la seule façon utile d'exprimer le seuil, qui est publié en revenu.
    const { abattement } = ACTIVITES[activite];
    const caMinimum = IJ.revenuMinimum / (1 - abattement);

    // Chiffre d'affaires à partir duquel l'indemnité plafonne.
    const caPlafond = IJ.revenuPlafond / (1 - abattement);

    return {
        revenuAnnuelMoyen,
        ouvre,
        montant,
        jours,
        joursIndemnises,
        total: montant * joursIndemnises,
        plafonne: revenuAnnuelMoyen >= IJ.revenuPlafond,
        caMinimum,
        caPlafond,
        carence: IJ.carence,
    };
}
